import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import API from '../config/api';
import QureLayout from '../components/qure/QureLayout';
import { analyticsPdfFilename, downloadAnalyticsPdf } from '../utils/analyticsReportExport';

const QureAnalytics = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    overdue: 0
  });
  const [showAnalyzeModal, setShowAnalyzeModal] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [currentAnalyticsData, setCurrentAnalyticsData] = useState(null);
  const [chatContext, setChatContext] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [autoLoadScheduleId, setAutoLoadScheduleId] = useState(null);

  useEffect(() => {
    fetchAnalyticsData();
    
    // Check for schedule_id in URL params (from Calendar page)
    const scheduleIdParam = searchParams.get('schedule_id');
    if (scheduleIdParam) {
      setAutoLoadScheduleId(scheduleIdParam);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('schedule_id');
      setSearchParams(newSearchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Auto-load analytics when schedule_id is provided from Calendar
  useEffect(() => {
    if (autoLoadScheduleId && !loading) {
      loadScheduleAnalytics(autoLoadScheduleId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoadScheduleId, loading]);
  
  const loadScheduleAnalytics = async (scheduleId) => {
    try {
      const scheduleRes = await API.get(`/qure/calendar/schedule-details/${scheduleId}`);
      if (scheduleRes.data.success && scheduleRes.data.schedule) {
        const schedule = scheduleRes.data.schedule;
        const experimentId = schedule.experiment_id;
        const filename = schedule.filename;
        const projectId = schedule.project_id;
        
        if (!filename) {
          setShowAnalyzeModal(true);
          setAnalyzeLoading(false);
          const resultsDiv = document.getElementById('analyticsResults');
          if (resultsDiv) {
            resultsDiv.innerHTML = `
              <div class="error-message" style="text-align: center; padding: 2rem;">
                <div style="font-size: 3rem; margin-bottom: 1rem;">📄</div>
                <h3 style="color: #6c757d; margin-bottom: 0.5rem;">No Log File Available</h3>
                <p style="color: #6c757d;">This experiment does not have a log file available for analysis.</p>
              </div>
            `;
          }
          return;
        }
        
        await handleAnalyze(scheduleId, experimentId, filename, projectId);
      } else {
        console.error('Schedule not found');
        setShowAnalyzeModal(true);
        setAnalyzeLoading(false);
        const resultsDiv = document.getElementById('analyticsResults');
        if (resultsDiv) {
          resultsDiv.innerHTML = `
            <div class="error-message">
              <strong>⚠️ Error:</strong> Schedule not found. Please try again.
            </div>
          `;
        }
      }
    } catch (error) {
      console.error('Error loading schedule analytics:', error);
      setShowAnalyzeModal(true);
      setAnalyzeLoading(false);
      const resultsDiv = document.getElementById('analyticsResults');
      if (resultsDiv) {
        resultsDiv.innerHTML = `
          <div class="error-message">
            <strong>⚠️ Error:</strong> ${escapeHtml(error.message || 'Failed to load analytics. Please try again.')}
          </div>
        `;
      }
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      
      // Fetch stats
      const statsRes = await API.get('/qure/analytics/stats');
      if (statsRes.data.success) {
        setStats(statsRes.data.stats || {
          total: 0,
          completed: 0,
          pending: 0,
          overdue: 0
        });
      }
      
      // Fetch projects with experiments
      const projectsRes = await API.get('/qure/analytics/projects');
      if (projectsRes.data.success) {
        setProjects(projectsRes.data.projects || []);
      }
      
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const escapeHtml = (text) => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds < 0) return '00:00:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const fetchLogFromS3 = async (experimentId, filename) => {
    try {
      const response = await API.get('/qure/analytics/fetch-log', {
        params: { experiment_id: experimentId, filename }
      });
      if (response.data.ok) {
        return response.data.content;
      }
      throw new Error('Failed to fetch log file');
    } catch (error) {
      console.error('Error fetching log:', error);
      throw error;
    }
  };

  const [analyzeProgress, setAnalyzeProgress] = useState({ stage: '', percent: 0 });
  const [exportPdfLoading, setExportPdfLoading] = useState(false);
  const [modalProtocolName, setModalProtocolName] = useState('Experiment Analysis');

  const handleExportPdf = async () => {
    if (!currentAnalyticsData || exportPdfLoading) return;
    setExportPdfLoading(true);
    try {
      await downloadAnalyticsPdf({
        apiPath: '/qure/analytics/export-pdf',
        analytics: currentAnalyticsData,
        account: {
          report_by: user?.username || user?.first_name || 'User',
          email: user?.email || '',
          trainee_name: user?.username || user?.first_name || '',
          protocol_name: modalProtocolName,
        },
        brand: 'qure',
        filename: analyticsPdfFilename(modalProtocolName),
      });
    } catch (error) {
      console.error('PDF export failed:', error);
      window.alert('Could not generate the analytics PDF. Please try again.');
    } finally {
      setExportPdfLoading(false);
    }
  };

  const handleAnalyze = async (scheduleId, experimentId, filename, projectId = null) => {
    try {
      setAnalyzeLoading(true);
      setShowAnalyzeModal(true);
      setCurrentAnalysis({
        scheduleId,
        experimentId,
        filename,
        projectId
      });
      setChatContext([]);
      setCurrentAnalyticsData(null);
      setAnalyzeProgress({ stage: 'Checking for existing analytics...', percent: 5 });
      
      console.log('=== FRONTEND: ANALYZE START ===');
      console.log('Parameters:', { scheduleId, experimentId, filename, projectId, isLive: window.location.hostname !== 'localhost' });
      
      // Check for existing analytics
      try {
        console.log('Checking for existing analytics...');
        const checkRes = await API.post('/qure/analytics/check-existing', {
          schedule_id: scheduleId
        });
        
        console.log('Check existing response:', checkRes.data);
        
        if (checkRes.data.ok && checkRes.data.exists && checkRes.data.analytics) {
          console.log('Found existing analytics, displaying...');
          setAnalyzeProgress({ stage: 'Loading cached analytics...', percent: 100 });
          displayAnalytics(checkRes.data.analytics);
          setAnalyzeLoading(false);
          return;
        }
      } catch (error) {
        console.error('Error checking existing analytics:', error);
      }
      
      console.log('No existing analytics found, proceeding to process...');
      setAnalyzeProgress({ stage: 'Fetching log file...', percent: 10 });
      
      // Fetch log from S3
      console.log('Fetching log file...');
      const logContent = await fetchLogFromS3(experimentId, filename);
      console.log('Log file fetched, length:', logContent?.length, 'characters');
      
      setAnalyzeProgress({ stage: 'Parsing log file...', percent: 20 });
      console.log('Processing experiment...');
      
      // Process experiment with extended timeout for large files
      setAnalyzeProgress({ stage: 'Parsing log file and identifying steps...', percent: 30 });
      
      const processRes = await API.post('/qure/analytics/process-experiment', {
        schedule_id: scheduleId,
        log_text: logContent
      }, {
        timeout: 300000 // 5 minutes for large files
      });
      
      console.log('Process response received:', processRes.data?.ok ? 'SUCCESS' : 'FAILED');
      
      if (processRes.data.ok) {
        setAnalyzeProgress({ stage: 'Rendering results...', percent: 90 });
        displayAnalytics(processRes.data);
        setAnalyzeProgress({ stage: 'Complete!', percent: 100 });
      } else {
        throw new Error(processRes.data.error || 'Failed to process experiment');
      }
      
    } catch (error) {
      console.error('Error analyzing experiment:', error);
      console.error('Error details:', error.response?.data || error.message);
      const resultsDiv = document.getElementById('analyticsResults');
      if (resultsDiv) {
        let errorMessage = error.message || 'Failed to analyze experiment';
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          errorMessage = 'Request timed out. The log file may be too large. Please try again or contact support.';
        }
        resultsDiv.innerHTML = `
          <div class="error-message">
            <strong>⚠️ Error:</strong> ${escapeHtml(errorMessage)}
          </div>
        `;
      }
    } finally {
      setAnalyzeLoading(false);
      setAnalyzeProgress({ stage: '', percent: 0 });
    }
  };

  const displayAnalytics = (data) => {
    setCurrentAnalyticsData(data);
    
    const schedule = data.schedule || {};
    const metrics = data.metrics || {};
    const reproducibility = data.reproducibility || {};
    const gemini = data.gemini || {};
    const summary = metrics.summary || {};
    
    const protocolName = schedule.protocol_name || 'Experiment Analysis';
    setModalProtocolName(protocolName);
    const protocolNameEl = document.getElementById('modalProtocolName');
    if (protocolNameEl) {
      protocolNameEl.textContent = protocolName;
    }
    
    const gradeClass = `grade-${reproducibility.grade || 'F'}`;
    
    let html = `
      <div class="analytics-content">
        <div class="metrics-section">
          <h3>📊 Overall Performance</h3>
          <div class="metrics-grid">
            <div class="metric-card">
              <div class="metric-label">Reproducibility Score</div>
              <div class="metric-value">${reproducibility.score || 0}/100</div>
              <div style="margin-top: 1rem;">
                <span class="grade-badge ${gradeClass}">${reproducibility.grade || 'F'}</span>
              </div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Total Time</div>
              <div class="metric-value">${formatTime(summary.total_time || 0)}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Steps Completed</div>
              <div class="metric-value">${summary.completed_steps || 0}/${summary.total_steps || 0}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Deviations</div>
              <div class="metric-value">${summary.actual_deviations || 0}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Confidence</div>
              <div class="metric-value">${Math.round((summary.avg_confidence || 0) * 100)}%</div>
            </div>
          </div>
        </div>
    `;
    
    // E-notebook
    if (gemini.ok && gemini.json && gemini.json.transcription) {
      html += `
        <div class="metrics-section">
          <h3>📝 E-Notebook Summary</h3>
          <div class="metric-card">
            <div style="line-height: 1.8;">
              ${escapeHtml(gemini.json.transcription).replace(/\n/g, '<br>')}
            </div>
          </div>
        </div>
      `;
    }
    
    // Score validation
    if (gemini.ok && gemini.json && gemini.json.reproducibility_validation) {
      html += `
        <div class="metrics-section">
          <h3>✅ Score Validation</h3>
          <div class="metric-card">
            ${escapeHtml(gemini.json.reproducibility_validation)}
          </div>
        </div>
      `;
    }
    
    // Deviations
    if (gemini.ok && gemini.json && gemini.json.deviations && gemini.json.deviations.length > 0) {
      html += `
        <div class="metrics-section">
          <h3>⚠️ Deviations Analysis</h3>
          <div style="display: flex; flex-direction: column; gap: 1rem;">
      `;
      
      gemini.json.deviations.forEach((dev, idx) => {
        html += `
          <div class="metric-card" style="border-left-color: var(--danger-red);">
            <h4 style="margin: 0 0 0.75rem 0; color: var(--danger-red); font-weight: 700;">
              Deviation ${idx + 1} - Step ${dev.step || 'N/A'}
            </h4>
            <p style="margin-bottom: 0.5rem;"><strong>Description:</strong> ${escapeHtml(dev.description || '')}</p>
            <p style="margin-bottom: 0.5rem;"><strong>Impact:</strong> ${escapeHtml(dev.impact || '')}</p>
            <p style="margin-bottom: 0;"><strong>Prevention:</strong> ${escapeHtml(dev.prevention || '')}</p>
          </div>
        `;
      });
      
      html += `</div></div>`;
    }
    
    // Step-by-step performance
    if (metrics.step_stats && Object.keys(metrics.step_stats).length > 0) {
      html += `
        <div class="metrics-section">
          <h3>📊 Step-by-Step Performance</h3>
          <div class="step-performance-list">
      `;
      
      Object.keys(metrics.step_stats).sort((a, b) => parseInt(a) - parseInt(b)).forEach(stepNum => {
        const step = metrics.step_stats[stepNum];
        const status = step.completion !== null ? 'completed' : 'incomplete';
        const statusIcon = status === 'completed' ? '✅' : '❌';
        
        html += `
          <div class="step-item ${status}">
            <div class="step-header">
              ${statusIcon} Step ${stepNum}: ${status.charAt(0).toUpperCase() + status.slice(1)}
            </div>
            <div class="step-details">
              <div><strong>Time:</strong> ${formatTime(step.time_to_complete || 0)}</div>
              <div><strong>Prompts:</strong> ${step.prompts || 0}</div>
              <div><strong>Confidence:</strong> ${Math.round((step.avg_confidence || 0) * 100)}%</div>
              <div><strong>Deviations:</strong> ${step.actual_deviations || 0}</div>
            </div>
          </div>
        `;
      });
      
      html += `</div></div>`;
    }
    
    html += `</div>`;
    
    const resultsDiv = document.getElementById('analyticsResults');
    if (resultsDiv) {
      resultsDiv.innerHTML = '';
      resultsDiv.innerHTML = html;
    }
    
    // Show right panel
    setTimeout(() => {
      const rightPanel = document.getElementById('rightPanel');
      if (rightPanel) {
        rightPanel.style.display = 'flex';
      }
    }, 100);
    
    // Setup upload zone
    const uploadZone = document.getElementById('resultsUploadZone');
    const fileInput = document.getElementById('resultsFileInput');
    if (uploadZone && fileInput) {
      uploadZone.onclick = () => fileInput.click();
      
      uploadZone.ondragover = (e) => {
        e.preventDefault();
        uploadZone.classList.add('drag-over');
      };
      
      uploadZone.ondragleave = () => {
        uploadZone.classList.remove('drag-over');
      };
      
      uploadZone.ondrop = (e) => {
        e.preventDefault();
        uploadZone.classList.remove('drag-over');
        if (e.dataTransfer.files.length > 0) {
          fileInput.files = e.dataTransfer.files;
          const event = new Event('change', { bubbles: true });
          fileInput.dispatchEvent(event);
        }
      };
    }
  };

  const handleResultsFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!currentAnalysis) {
      alert('Please analyze an experiment first');
      return;
    }
    
    const statusDiv = document.getElementById('resultsUploadStatus');
    const uploadZone = document.getElementById('resultsUploadZone');
    
    if (uploadZone) uploadZone.classList.add('uploading');
    if (statusDiv) {
      statusDiv.innerHTML = '<div style="text-align: center; color: #17a2b8; padding: 0.75rem; font-size: 0.9rem;">📤 Uploading and analyzing...</div>';
    }
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('schedule_id', currentAnalysis.scheduleId || '');
      formData.append('message', 'Analyze this results file and determine if it is relevant to the experiment');
      
      if (currentAnalyticsData) {
        formData.append('analyticsData', JSON.stringify([currentAnalyticsData]));
      }
      
      const response = await API.post('/qure/analytics/upload-results', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (uploadZone) uploadZone.classList.remove('uploading');
      
      if (response.data.ok) {
        const data = response.data;
        let message = `📊 **Results Analyzed:** ${data.response}`;
        
        if (data.is_relevant && data.results_saved) {
          message = `✅ **File Saved to Knowledge Base!**\n\n${data.response}`;
        } else if (data.is_relevant && !data.results_saved) {
          message = `⚠️ **File Relevant but Not Saved**\n\n${data.response}`;
          if (data.save_error) {
            message += `\n\n**Error:** ${data.save_error}`;
          }
        } else if (!data.is_relevant) {
          message = `ℹ️ **Not Relevant to Experiment**\n\n${data.response}`;
        }
        
        addMessageToChat('bot', message);
        if (statusDiv) statusDiv.innerHTML = '';
      } else {
        throw new Error(response.data.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading results:', error);
      if (uploadZone) uploadZone.classList.remove('uploading');
      if (statusDiv) {
        statusDiv.innerHTML = `
          <div class="upload-status error">
            <strong>❌ Error:</strong> ${escapeHtml(error.message || 'Upload failed')}
          </div>
        `;
        setTimeout(() => {
          statusDiv.innerHTML = '';
        }, 5000);
      }
    }
    
    const fileInputEl = document.getElementById('resultsFileInput');
    if (fileInputEl) fileInputEl.value = '';
  };

  const addMessageToChat = (sender, text) => {
    setChatContext(prev => [...prev, { sender, text }]);
    setTimeout(() => {
      const messagesDiv = document.getElementById('chatbotMessages');
      if (messagesDiv) {
        messagesDiv.scrollTop = messagesDiv.scrollHeight;
      }
    }, 100);
  };

  const sendChatMessage = async () => {
    const message = chatInput.trim();
    if (!message || chatLoading) return;
    
    setChatInput('');
    addMessageToChat('user', message);
    setChatLoading(true);
    
    try {
      const response = await API.post('/qure/analytics/chat', {
        message: message,
        full_context: JSON.stringify(currentAnalyticsData || {}),
        chat_history: chatContext,
        analyticsData: currentAnalyticsData ? [currentAnalyticsData] : null,
        project_id: currentAnalysis?.projectId || null,
        schedule_id: currentAnalysis?.scheduleId || null
      });
      
      if (response.data.ok) {
        addMessageToChat('bot', response.data.response);
      } else {
        addMessageToChat('bot', `⚠️ Error: ${response.data.error || 'Failed to get response'}`);
      }
    } catch (error) {
      console.error('Error sending chat message:', error);
      addMessageToChat('bot', `⚠️ Error: ${error.message || 'Failed to send message'}`);
    } finally {
      setChatLoading(false);
    }
  };

  const closeAnalyzeModal = () => {
    setShowAnalyzeModal(false);
    setCurrentAnalysis(null);
    setChatContext([]);
    const resultsDiv = document.getElementById('analyticsResults');
    if (resultsDiv) resultsDiv.innerHTML = '';
    const messagesDiv = document.getElementById('chatbotMessages');
    if (messagesDiv) {
      messagesDiv.innerHTML = `
        <div class="chatbot-message">
          <div class="message-bubble bot">
            <strong>Hello!</strong> I can help you analyze this experiment, generate research paper content, or answer questions about the data.
          </div>
        </div>
      `;
    }
  };

  if (loading) {
    return (
      <QureLayout>
        <div className="analytics-page">
          <div className="loading-spinner">Loading analytics...</div>
        </div>
      </QureLayout>
    );
  }

  return (
    <QureLayout>
      <div className="analytics-page">
        <div className="analytics-header">
          <h1>🔬 Experiment Analytics Dashboard</h1>
          <p>Comprehensive analysis and insights from your laboratory experiments</p>
        </div>

        <div className="performance-section">
          <div className="performance-header">
            <h2>📈 Your Performance Metrics</h2>
          </div>
          
          <div className="performance-grid">
            <div className="performance-card blue">
              <div className="perf-icon">📋</div>
              <div className="perf-number">{stats.total || 0}</div>
              <div className="perf-label">Total Scheduled</div>
            </div>
            
            <div className="performance-card green">
              <div className="perf-icon">✅</div>
              <div className="perf-number">{stats.completed || 0}</div>
              <div className="perf-label">Completed</div>
            </div>
            
            <div className="performance-card yellow">
              <div className="perf-icon">⏰</div>
              <div className="perf-number">{stats.pending || 0}</div>
              <div className="perf-label">Pending</div>
            </div>
            
            <div className="performance-card red">
              <div className="perf-icon">⚠️</div>
              <div className="perf-number">{stats.overdue || 0}</div>
              <div className="perf-label">Overdue</div>
            </div>
          </div>
        </div>

        {/* Projects with Experiments */}
        {projects.length > 0 ? (
          projects.map(project => (
            <div key={project.project_id} className="project-experiments-card">
              <div className="project-heading-bar">
                <h2>📂 {project.project_name}</h2>
              </div>
              
              <div className="experiments-table-wrapper">
                <table className="experiments-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Protocol</th>
                      <th>User</th>
                      <th>Log File</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {project.experiments?.map(exp => (
                      <tr key={exp.schedule_id}>
                        <td>{formatDate(exp.scheduled_date)}</td>
                        <td>{exp.protocol_name}</td>
                        <td>{exp.username}</td>
                        <td>{exp.filename}</td>
                        <td>
                          <button 
                            className="btn-analyze" 
                            onClick={() => handleAnalyze(exp.schedule_id, exp.experiment_id, exp.filename, project.project_id)}
                          >
                            🔍 Analyze
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        ) : (
          <div className="performance-section">
            <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#6c757d' }}>
              <div style={{ fontSize: '5rem', marginBottom: '1.5rem' }}>📭</div>
              <h3 style={{ color: '#2C3E50', fontSize: '1.5rem', marginBottom: '0.5rem' }}>No Experiments Available</h3>
              <p>Complete some experiments in your projects to see comprehensive analytics here.</p>
            </div>
          </div>
        )}

        {/* Executive Summary */}
        <div className="executive-summary">
          <h3>📊 Executive Summary</h3>
          <p>
            Welcome, <strong>{user?.username || user?.first_name || 'User'}</strong>! 
            You have successfully completed <strong>{stats.completed || 0} Scheduled experiments</strong> across 
            <strong> {stats.total || 0} experiments</strong>. 
            and <strong> {stats.pending || 0} pending experiments </strong> and you have <strong> missed {stats.overdue || 0} experiments</strong> to date.
          </p>
        </div>

        {/* Analyze Modal */}
        {showAnalyzeModal && (
          <div className="fullpage-modal active">
            <div className="modal-qurator-header">
              <h2>🔬 <span id="modalProtocolName">{modalProtocolName}</span></h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                {currentAnalyticsData && !analyzeLoading && (
                  <button
                    type="button"
                    className="btn-analyze"
                    onClick={handleExportPdf}
                    disabled={exportPdfLoading}
                    style={{ background: '#0891b2', borderColor: '#0891b2' }}
                  >
                    {exportPdfLoading ? 'Generating PDF…' : 'Export PDF'}
                  </button>
                )}
                <button className="btn-close-fullpage" onClick={closeAnalyzeModal}>×</button>
              </div>
            </div>
            
            <div className="modal-content-wrapper">
              <div className="details-main-content">
                <div id="analyticsResults">
                  {analyzeLoading ? (
                    <div className="loading-spinner" style={{ textAlign: 'center', padding: '2rem' }}>
                      <div style={{ marginBottom: '1rem' }}>
                        <div className="spinner-animation" style={{ width: '60px', height: '60px', border: '4px solid #e9ecef', borderTop: '4px solid #E94E1B', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }}></div>
                      </div>
                      <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#2C3E50', marginBottom: '0.5rem' }}>
                        {analyzeProgress.stage || 'Parsing log file...'}
                      </div>
                      <div style={{ marginBottom: '0.5rem' }}>
                        Processing... ({analyzeProgress.percent > 0 ? `${analyzeProgress.percent}%` : '...'} elapsed)
                      </div>
                      <div style={{ width: '200px', height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', margin: '0 auto', overflow: 'hidden' }}>
                        <div style={{ width: `${analyzeProgress.percent || 20}%`, height: '100%', backgroundColor: '#E94E1B', borderRadius: '4px', transition: 'width 0.3s ease' }}></div>
                      </div>
                      <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#6c757d' }}>
                        🔄 {analyzeProgress.stage || 'Parsing log file and identifying steps...'}
                      </div>
                      <div style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: '#adb5bd', fontStyle: 'italic' }}>
                        Processing... ({analyzeProgress.percent > 0 ? `${analyzeProgress.percent}s` : '25s'} elapsed)
                      </div>
                    </div>
                  ) : (
                    <div className="loading-spinner" style={{ display: 'none' }}>Loading analytics...</div>
                  )}
                </div>
              </div>
              
              {/* Right Panel for Results Upload & Chatbot */}
              <div className="right-panel" id="rightPanel" style={{ display: 'none' }}>
                {/* Upload Results Section */}
                <div className="upload-results-section">
                  <div className="section-header">
                    <span>📤 Upload Results</span>
                  </div>
                  <div className="section-body">
                    <div id="resultsUploadZone" className="upload-zone">
                      <div className="upload-icon">📄</div>
                      <div className="upload-text"><strong>Drop files here</strong> or click to browse</div>
                      <div className="upload-text" style={{ fontSize: '0.85rem', color: '#ADB5BD' }}>
                        Supports: CSV, Excel, PDF, Images
                      </div>
                    </div>
                    <input 
                      type="file" 
                      id="resultsFileInput" 
                      className="file-input"
                      accept=".csv,.xlsx,.xls,.pdf,.jpg,.jpeg,.png,.gif,image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => handleResultsFileSelect(e)}
                    />
                    <div id="resultsUploadStatus"></div>
                  </div>
                </div>

                {/* Chatbot Section */}
                <div className="chatbot-section">
                  <div className="section-header">
                    <span>💬 AI Assistant</span>
                    <button className="btn-clear-chat" onClick={() => setChatContext([])}>Clear</button>
                  </div>
                  <div className="section-body">
                    <div id="chatbotMessages" className="chatbot-messages">
                      <div className="chatbot-message">
                        <div className="message-bubble bot">
                          <strong>Hello!</strong> I can help you analyze this experiment, generate research paper content, or answer questions about the data.
                        </div>
                      </div>
                      {chatContext.map((msg, index) => (
                        <div key={index} className="chatbot-message">
                          <div className={`message-bubble ${msg.sender}`}>
                            {msg.text}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div id="typingIndicator" className="typing-indicator" style={{ display: chatLoading ? 'block' : 'none' }}>
                      AI is typing...
                    </div>
                    <div className="chat-input-area">
                      <textarea 
                        id="chatbotInput" 
                        className="chat-input"
                        placeholder="Ask about this experiment..." 
                        rows="1"
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            sendChatMessage();
                          }
                        }}
                      />
                      <button 
                        id="chatbotSendBtn" 
                        className="btn-chat-send" 
                        onClick={sendChatMessage}
                        disabled={chatLoading || !chatInput.trim()}
                      >
                        Send
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </QureLayout>
  );
};

export default QureAnalytics;
